import React, { useCallback, useEffect, useMemo, useState } from 'react';

import currencyFormatter from 'currency-formatter';
import { Helmet } from 'react-helmet';
import { Link as NavLink } from '~/lib/react-router-dom';

import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { isOnNewExpiryProcess } from '~/consts/brands';
import { ROLE_TOUR_COORDINATOR } from '~/consts/roles';

import useCurrentTenant from '~/hooks/useCurrentTenant';
import useCurrentUser from '~/hooks/useCurrentUser';

import {
  addYears,
  formatDateLongISO,
  formatDateLongMonthWithMeridiemNoSeconds,
  formatDateWeekDayShortMonth,
  formatDateWithClock,
} from '~/services/TimeService';

import { isAdmin } from '~/utils/adminPermission';
import isProd from '~/utils/isProd';

import GridPagination from '../../Common/Elements/GridPagination';
import PageSubheader from '../../Common/Elements/PageSubheader';
import CreditsForm from '../../Common/Forms/CreditsForm';
import EligibleCreditReversalsModal from '../CreditReversals/EligibleCreditReversalsModal';

import ChangeExpiryDateModal from './ChangeExpiryDateModal';
import { BasicUser, Credit, Credits } from './types';

type Props = {
  currency: string;
  user: App.User;
  idUser: string;
  currentPage: number;
  initiators: Array<BasicUser>;
  credits: Credits;
  creditTypes: unknown;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  rerender: () => void;
};

export default function CreditsPage({
  currency,
  user,
  idUser,
  currentPage,
  initiators,
  credits,
  creditTypes,
  isLoading,
  onPageChange,
  rerender,
}: Props) {
  const { tenant } = useCurrentTenant();
  const { user: currentUser } = useCurrentUser();
  const currencyFormat = useCallback(
    (amount: number) => {
      return currencyFormatter.format(amount, {
        code: currency,
        precision: 2,
      });
    },
    [currency],
  );

  const hasAdminCreditPermissions = useMemo(() => {
    return isAdmin(currentUser) || currentUser?.roles?.includes(ROLE_TOUR_COORDINATOR);
  }, [currentUser]);

  const getLatestDate = useCallback((dates: Array<{ expires_at: string }>) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore because TS does't like dates and math
    return formatDateLongISO(Math.max(...dates.map((e) => new Date(e.expires_at))));
  }, []);

  // the expiry date for all user's credits is the latest date from all of their credits,
  // or 1 year from now if there are no credits
  const calculateExpiryDate = useCallback(() => {
    if (credits.result.collection.length > 0) {
      return getLatestDate(credits.result.collection);
    } else {
      return formatDateLongISO(addYears(1));
    }
  }, [credits.result.collection, getLatestDate]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [creditReversalModalOpen, setCreditReversalModalOpen] = useState<boolean>(false);
  const [creditsSubtitle, setCreditsSubtitle] = useState<string>('');
  const [allExpiresAt, setAllExpiresAt] = useState<string>('');
  const [changeExpiryDateRequestState, setChangeExpiryDateRequestState] = useState<Utils.FetchingState>('idle');

  useEffect(() => {
    setCreditsSubtitle(currencyFormat(Number(credits.metadata.available_credit)));
    setAllExpiresAt(calculateExpiryDate());
  }, [calculateExpiryDate, credits, currencyFormat]);

  const creditFormat = useCallback(
    (value: string) => {
      const amount = parseFloat(value);
      return amount > 0 ? currencyFormat(amount) : '-';
    },
    [currencyFormat],
  );

  const debitFormat = useCallback(
    (value: string) => {
      const amount = parseFloat(value);
      return amount < 0 ? currencyFormat(Math.abs(amount)) : '-';
    },
    [currencyFormat],
  );

  const addedByLink = useCallback(
    (fk_user_id: string) => {
      const initiator = initiators.find((i) => i.id_member === fk_user_id);
      return (
        <Link component={NavLink} to={`/users/${fk_user_id}`}>
          {initiator ? initiator.fullName : 'Unknown'}
        </Link>
      );
    },
    [initiators],
  );

  const creditComments = useCallback(
    (comments: string, { fk_orders }: { fk_orders: string }) => (
      <Box overflow="hidden">
        <Typography whiteSpace="normal">{comments}</Typography>
        {fk_orders && (
          <Typography noWrap>
            Order:{' '}
            <Link component={NavLink} to={`/purchases/${fk_orders}`}>
              {fk_orders}
            </Link>
          </Typography>
        )}
      </Box>
    ),
    [],
  );

  const earnableFormat = useCallback((amount: string, is_societe_earnable: boolean | null) => {
    if (Number(amount) <= 0) {
      return '-';
    }

    return is_societe_earnable ? 'Yes' : 'No';
  }, []);

  const toggleCreditExtensionModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  const expiresAtDateValueFormatter = useCallback(
    (value: string, params: Credit) => {
      if (!isAdmin(currentUser) && params.credit_type.toLocaleLowerCase() === 'spent') {
        return '-';
      }
      if (isOnNewExpiryProcess(tenant.brand)) {
        return formatDateWithClock(value);
      } else {
        return formatDateWithClock(allExpiresAt);
      }
    },
    [allExpiresAt, currentUser, tenant.brand],
  );

  const columns: Array<GridColDef<Credit>> = [
    {
      field: 'created_at',
      headerName: 'Created Date',
      sortable: false,
      width: 150,
      valueFormatter: (value) => {
        return formatDateWeekDayShortMonth(value);
      },
      display: 'flex',
    },
    {
      field: 'amount_credit',
      headerName: 'Credit',
      sortable: false,
      width: 120,
      renderCell: (params) => {
        return creditFormat(params.row.amount);
      },
      display: 'flex',
    },
    {
      field: 'amount_debit',
      headerName: 'Debit',
      sortable: false,
      width: 120,
      renderCell: (params) => {
        return debitFormat(params.row.amount);
      },
      display: 'flex',
    },
    {
      field: 'fk_added_by',
      headerName: 'Added by',
      sortable: false,
      width: 200,
      renderCell: (params) => addedByLink(params.value),
      display: 'flex',
    },
    {
      field: 'credit_type',
      headerName: 'Credit type',
      sortable: false,
      width: 180,
      display: 'flex',
      renderCell: (params) =>
        params.row.credit_type === 'Agent Hub' ? 'Agent Hub, Commercial team only' : params.row.credit_type,
    },
    {
      field: 'expires_at',
      headerName: 'Expiry Date',
      sortable: false,
      valueFormatter: (value: string, params: Credit) => expiresAtDateValueFormatter(value, params),
      width: 180,
      display: 'flex',
    },
    {
      field: 'comments',
      headerName: 'Comment',
      sortable: false,
      flex: 1,
      renderCell: (params) => creditComments(params.value, params.row),
      display: 'flex',
    },
    {
      field: 'currency',
      headerName: 'Currency',
      display: 'flex',
    },
    {
      field: 'is_societe_earnable',
      headerName: 'Earnable (Société)',
      display: 'flex',
      sortable: false,
      width: 180,
      renderCell: (params) => {
        return earnableFormat(params.row.amount, params.row.is_societe_earnable);
      },
    },
  ];

  return (
    <div>
      <Helmet>
        <title>Users | {user.fullName} | Credits</title>
      </Helmet>
      <PageSubheader title="Add credits" />
      <CreditsForm
        credits={credits}
        creditTypes={creditTypes}
        currency={currency}
        id_user={idUser}
        rerender={rerender}
        updateAllExpiryDate={setAllExpiresAt}
      />
      {!isLoading && <PageSubheader title={`Total Credit: ${creditsSubtitle}`} />}
      <Box my={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {!isOnNewExpiryProcess(tenant.brand) && (
            <Stack flexGrow={1} direction="row" spacing={1}>
              <Typography variant="h6">All credit expires at:</Typography>
              <Typography variant="h6" color="textSecondary" fontWeight="normal">
                {formatDateLongMonthWithMeridiemNoSeconds(allExpiresAt)}
              </Typography>
            </Stack>
          )}
          {isOnNewExpiryProcess(tenant.brand) && credits.result.led_next_expiring && (
            <Stack flexGrow={1} direction="row" spacing={1}>
              <Typography variant="h6">
                Next credit (
                {currencyFormatter.format(Number(credits.result.led_next_expiring.amount), {
                  code: currency,
                  precision: 2,
                })}
                ) expires at:
              </Typography>
              <Typography variant="h6" color="textSecondary" fontWeight="normal">
                {formatDateLongMonthWithMeridiemNoSeconds(credits.result.led_next_expiring.expires_at)}
              </Typography>
            </Stack>
          )}
          {isOnNewExpiryProcess(tenant.brand) && !credits.result.led_next_expiring && (
            <Stack flexGrow={1} direction="row" spacing={1}>
              <Typography variant="h6">No credits to expire</Typography>
            </Stack>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isProd() && (
              <Button type="button" onClick={() => setCreditReversalModalOpen(true)} variant="contained">
                Reverse Credits
              </Button>
            )}
            <Button
              type="submit"
              onClick={toggleCreditExtensionModal}
              variant="contained"
              disabled={changeExpiryDateRequestState === 'loading'}
            >
              {hasAdminCreditPermissions ? 'Change expiry date' : 'View Active Credits'}
            </Button>
          </Box>
        </Stack>
      </Box>
      <DataGrid
        rows={credits.result.collection}
        rowCount={credits.metadata.total_available}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 50,
            },
          },
        }}
        loading={isLoading}
        paginationModel={{ page: currentPage - 1, pageSize: 50 }}
        paginationMode="server"
        pageSizeOptions={[50]}
        getRowId={(row) => row.id_credit}
        getRowHeight={() => 'auto'}
        onPaginationModelChange={({ page }) => onPageChange(page + 1)}
        slots={{ pagination: GridPagination }}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        autoHeight
      />
      {isModalOpen && (
        <ChangeExpiryDateModal
          setChangeExpiryDateRequestState={setChangeExpiryDateRequestState}
          userId={idUser}
          setIsModalOpen={setIsModalOpen}
          rerender={rerender}
          isModalOpen={isModalOpen}
          currency={currency}
          isOnNewExpiryProcess={isOnNewExpiryProcess}
        />
      )}
      {!isProd() && (
        <EligibleCreditReversalsModal
          open={creditReversalModalOpen}
          onClose={() => setCreditReversalModalOpen(false)}
          userId={idUser}
          currency={currency}
        />
      )}
    </div>
  );
}
